import "./App.scss";
import { Provider } from "mobx-react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    Outlet,
} from "react-router-dom";
import { normalRoutes, routes } from "./router";
import { useEffect, useState } from "react";
import authenticationStore from "./store/authenticationStore";
import workScheduleStore from "./store/workScheduleStore";
import notificationStore from "./store/notificationStore";
import dashBoardStore from "./store/dashBoardStore";
import fileStore from "./store/fileStore";
import roleStore from "./store/roleStore";
import userStore from "./store/userStore";
import taskStore from "./store/taskStore";
import aclStore from "./store/aclStore";
import commandStore from "./store/commandStore";
import workStore from "./store/workStore";
import tintucStore from "./store/tintucStore";
import companyStore from "./store/companyStore";
import moduleStore from "./store/moduleStore";
import { useSSO } from "./config";
import { ReactKeycloakProvider, useKeycloak } from "@react-keycloak/web";
import keycloak from "./keycloak";
import { createBrowserHistory } from "history";
import axios from "axios";
import moment from "moment";
import accountStore from "./store/accountStore";
import internalDocumentStore from "./store/internalDocumentStore";
import { DocumentPage } from "./pages";
import InternalDocumentOutgoing from "./modules/InternalDocumentOutgoing/InternalDocumentOutgoing";
import { InternalDocumentIncoming } from "./modules/InternalDocumentIncoming";
import { InternalDocumentOutgoingDetail } from "./modules/InternalDocumentOutgoing";
import NotFoundPage from "./pages/NotFoundPage";
import { app } from "./firebase";
import { getMessaging } from "firebase/messaging";

const PrivateRoute = () => {
    return localStorage.getItem("jwt") ||
        sessionStorage.getItem("jwt") ||
        sessionStorage.getItem("jwt") ? (
        <Outlet />
    ) : (
        <Navigate to="/login" />
    );
};
const rootStore = {
    authenticationStore,
    dashBoardStore,
    notificationStore,
    workScheduleStore,
    roleStore,
    internalDocumentStore,
    fileStore,
    userStore,
    aclStore,
    accountStore,
    workStore,
    tintucStore,
    taskStore,
    companyStore,
    moduleStore,
    commandStore,
};

const history = createBrowserHistory();
axios.defaults.timeout = 60000;
axios.interceptors.request.use(
    (config) => config,
    (error) => Promise.reject(error)
);
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (axios.isCancel(error)) {
            return Promise.resolve();
        }
        if (error?.response?.status === 408 || error?.code === "ECONNABORTED") {
            authenticationStore.userLogout();
            history.push("/login");
        }

        var errorMessage = error?.response?.data?.errorMessage?.messages;
        if (errorMessage) {
            errorMessage.errorCode =
                error?.response?.data?.errorMessage?.errorCode;
        }

        return Promise.reject(
            errorMessage || {
                vi: "Đã có lỗi xảy ra trong quá trình kết nối!",
            }
        );
    }
);

moment.locale("vi", {
    week: {
        dow: 1,
    },
});

const saveToken = (tokenValue) => {
    if (tokenValue) {
        let objToken = {
            access_token: tokenValue,
        };
        localStorage.setItem("jwt", JSON.stringify(objToken));
        authenticationStore.setAppToken(JSON.stringify(objToken));
    }
};

function App() {
    const [ready, setReady] = useState(false);
    const eventLogger = (event, error) => {
        if (event.toString() === "onReady") {
            setReady(true);
        }
    };
    const tokenLogger = (tokens) => {
        // console.log('tokenLogger', tokens)

        if (tokens.token) {
            saveToken(tokens.token);
        }
    };
    const keycloakProviderInitConfig = {
        onLoad: "login-required",
        silentCheckSsoRedirectUri:
            window.location.origin + "/assets/silent-check-sso.html",
    };
    return useSSO ? (
        <ReactKeycloakProvider
            authClient={keycloak}
            initOptions={keycloakProviderInitConfig}
            onEvent={eventLogger}
            onTokens={tokenLogger}
        >
            {ready ? <AppRouter /> : <p />}
        </ReactKeycloakProvider>
    ) : (
        <AppRouter />
    );
}
export const AppRouter = () => {
    const fakeKeycloak = {
        keycloak: { token: authenticationStore.appToken },
    };
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { keycloak, initialized } = useSSO ? useKeycloak() : fakeKeycloak;
    useEffect(() => {
        console.log("useSSO", useSSO, process.env.REACT_APP_USE_SSO);
    }, [useSSO]);

    useEffect(
        () => {
            // console.log('useEffect. keycloak.token', keycloak.token)
            if (useSSO) {
                saveToken(keycloak.token);
            }
        },
        useSSO ? [keycloak.token] : [authenticationStore.appToken]
    );

    useEffect(
        () => {
            // console.log('useEffect. check session login')
            if (!keycloak.token) return;
            (async () => {
                // loadingAnimationStore.showSpinner(true);
                try {
                    const { data } =
                        await authenticationStore.checkCurrentUser();
                    await Promise.all([
                        aclStore.getACLDetailByUser(data.username),
                        commandStore.getCommandList(),
                        accountStore.getCurrentUserAccount(),
                        moduleStore.getModuleList(),
                        //     companyStore.getCompanyList(),
                    ]);
                } catch (err) {
                    history.push("/login");
                    localStorage.clear();
                } finally {
                    // loadingAnimationStore.showSpinner(false);
                }
            })();
        },
        useSSO ? [keycloak.token] : [authenticationStore.appToken]
    );

    useEffect(
        () => {
            if (!keycloak.token) return; // loadingAnimationStore.showSpinner(true)
            (async () => {
                try {
                    await Promise.allSettled([
                        // userStore.getMentionUserList(),
                        notificationStore.getUnreadNotificationCount(),
                        notificationStore.getUnreadNewsCount(),
                        authenticationStore.getCurrentUserAvatar(),
                    ]);
                } catch (err) {
                    console.log(err);
                    // message.error(err?.vi || 'Đã có lỗi xảy ra!')
                } finally {
                    // loadingAnimationStore.showSpinner(false)
                }
            })();
        },
        useSSO ? [keycloak.token] : [authenticationStore.appToken]
    );

    return (
        <Provider {...rootStore}>
            <Router>
                <Routes>
                    {normalRoutes.map((route) => (
                        <Route
                            exact
                            key={route.path}
                            path={route.path}
                            element={route.component}
                        />
                    ))}
                    <Route exact path="/" element={<PrivateRoute />}>
                        {routes.map((route) => (
                            <Route
                                exact
                                key={route.path}
                                path={route.path}
                                element={route.component}
                            />
                        ))}
                        <Route
                            exact
                            key={"/internal-document/"}
                            path={"/internal-document/"}
                            element={<DocumentPage />}
                        >
                            <Route
                                exact
                                key={"incoming-document"}
                                path={"incoming-document"}
                                element={<InternalDocumentIncoming />}
                            />
                            <Route
                                exact
                                key={"outgoing-document"}
                                path={"outgoing-document"}
                                element={<InternalDocumentOutgoing />}
                            />
                        </Route>
                        <Route path="*" element={<NotFoundPage />} />
                    </Route>
                </Routes>
            </Router>
        </Provider>
    );
};

export default App;
